import React, { useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import {
  COLOR_BRAND_ACCENT,
  COLOR_BRAND_PRIMARY,
  COLOR_BRAND_PRIMARY_HOVER,
  COLOR_SEMANTIC_ERROR_LIGHT,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SECONDARY,
  COLOR_TEXT_STRONG,
  COLOR_UI_BACKGROUND,
  COLOR_UI_BORDER,
  COLOR_UI_SURFACE,
  COLOR_UI_SURFACE_HOVER,
  FONT_BASE,
  RADIUS_MD,
  SPACING_MD,
  SPACING_SM,
  SPACING_XS,
} from '@src/styles/generated-theme-values'
import { BuzzMessagingState } from '../hooks/use-buzz-messaging'

const Shell = styled.section`
  height: calc(100vh - 132px);
  min-height: 520px;
  max-height: calc(100vh - 132px);
  display: grid;
  grid-template-columns: minmax(220px, 270px) minmax(360px, 1fr) minmax(220px, 280px);
  overflow: hidden;
  background: ${COLOR_UI_BACKGROUND};
  color: ${COLOR_TEXT_PRIMARY};
  border-top: 1px solid ${COLOR_UI_BORDER};
  font-family: ${FONT_BASE};

  @media (max-width: 1024px) {
    grid-template-columns: 220px minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr) minmax(180px, 240px);
  }

  @media (max-width: 720px), (max-width: 1024px) and (max-height: 600px) {
    display: flex;
    flex-direction: column;
    height: auto;
    min-height: calc(100vh - 96px);
    max-height: none;
    overflow: visible;
  }
`

const Rail = styled.aside`
  border-right: 1px solid ${COLOR_UI_BORDER};
  background: ${COLOR_UI_SURFACE};
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 720px), (max-width: 1024px) and (max-height: 600px) {
    border-right: 0;
    border-bottom: 1px solid ${COLOR_UI_BORDER};
    max-height: none;
    overflow: visible;
  }
`

const TeamRail = styled(Rail)`
  border-right: 0;
  border-left: 1px solid ${COLOR_UI_BORDER};

  @media (max-width: 1024px) {
    grid-column: 1 / -1;
    grid-row: 2;
    border-top: 1px solid ${COLOR_UI_BORDER};
    border-left: 0;
  }

  @media (max-width: 720px), (max-width: 1024px) and (max-height: 600px) {
    max-height: none;
    overflow: visible;
    border-top: 0;
    border-bottom: 0;
  }
`

const RailHeader = styled.div`
  padding: ${SPACING_MD};
  border-bottom: 1px solid ${COLOR_UI_BORDER};
`

const CommunityName = styled.h1`
  font-size: 17px;
  line-height: 1.3;
  margin: 0 0 4px;
  color: ${COLOR_TEXT_STRONG};
  font-family: 'NunitoSansSemiBold';
`

const Metadata = styled.div`
  color: ${COLOR_TEXT_SECONDARY};
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 12px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${COLOR_TEXT_SECONDARY};
  font-family: 'NunitoSansSemiBold';
`

const ThreadList = styled.ul`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  margin: 0;
  padding: ${SPACING_XS};
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ChannelList = styled.ul`
  max-height: 150px;
  overflow-y: auto;
  overscroll-behavior: contain;
  margin: 0;
  padding: ${SPACING_XS};
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-bottom: 1px solid ${COLOR_UI_BORDER};
`

const CollectionListItem = styled.li`
  min-width: 0;
`

const ThreadButton = styled.button<{ $active: boolean }>`
  width: 100%;
  min-height: 40px;
  padding: 8px 10px;
  border: 1px solid ${({ $active }) => $active ? COLOR_BRAND_PRIMARY : 'transparent'};
  border-radius: ${RADIUS_MD};
  background: ${({ $active }) => $active ? COLOR_UI_SURFACE_HOVER : 'transparent'};
  color: ${COLOR_TEXT_PRIMARY};
  text-align: left;
  font: inherit;
  cursor: pointer;

  &:hover { background: ${COLOR_UI_SURFACE_HOVER}; }
  &:focus-visible { outline: 2px solid ${COLOR_BRAND_ACCENT}; outline-offset: 1px; }
`

const ChannelButton = styled(ThreadButton)`
  min-height: 34px;
  padding: 6px 10px;
  font-family: 'NunitoSansSemiBold';
`

const ActionButton = styled.button`
  min-height: 36px;
  padding: 7px 12px;
  border: 1px solid ${COLOR_BRAND_PRIMARY};
  border-radius: ${RADIUS_MD};
  background: ${COLOR_BRAND_PRIMARY};
  color: ${COLOR_TEXT_STRONG};
  font: inherit;
  font-family: 'NunitoSansSemiBold';
  cursor: pointer;

  &:hover:not(:disabled) { background: ${COLOR_BRAND_PRIMARY_HOVER}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid ${COLOR_TEXT_STRONG}; outline-offset: 2px; }
`

const NewThreadForm = styled.form`
  display: flex;
  gap: 6px;
  padding: 8px;
`

const CompactInput = styled.input`
  width: 100%;
  min-width: 0;
  border: 1px solid ${COLOR_UI_BORDER};
  border-radius: ${RADIUS_MD};
  background: ${COLOR_UI_BACKGROUND};
  color: ${COLOR_TEXT_STRONG};
  padding: 8px;
  font: inherit;
  &:focus-visible { outline: 2px solid ${COLOR_BRAND_ACCENT}; }
`

const Conversation = styled.div`
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(260px, 1fr) auto;
  background: ${COLOR_UI_BACKGROUND};

  @media (max-width: 1024px) {
    grid-template-rows: auto minmax(0, 1fr) auto;
  }
`

const ConversationHeader = styled.header`
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px ${SPACING_MD};
  border-bottom: 1px solid ${COLOR_UI_BORDER};
`

type MessagingStatusTone = 'ready' | 'pending' | 'error'

const Status = styled.span<{ $tone: MessagingStatusTone }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ $tone }) => $tone === 'error' ? COLOR_SEMANTIC_ERROR_LIGHT : COLOR_TEXT_SECONDARY};
  font-size: 12px;
  white-space: nowrap;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $tone }) => $tone === 'ready' ? COLOR_BRAND_PRIMARY : $tone === 'error' ? COLOR_SEMANTIC_ERROR_LIGHT : COLOR_BRAND_ACCENT};
  }
`

const Timeline = styled.div`
  overflow-y: auto;
  padding: ${SPACING_MD};
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const EmptyState = styled.div`
  margin: auto;
  max-width: 420px;
  padding: 32px;
  text-align: center;
  color: ${COLOR_TEXT_SECONDARY};
`

const Message = styled.article`
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  gap: 10px;
`

const Avatar = styled.div<{ $agent?: boolean }>`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: ${({ $agent }) => $agent ? COLOR_BRAND_ACCENT : COLOR_BRAND_PRIMARY};
  color: ${COLOR_TEXT_STRONG};
  font-family: 'NunitoSansBold';
  font-size: 12px;
`

const MessageHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
`

const Author = styled.strong`
  color: ${COLOR_TEXT_STRONG};
  font-size: 14px;
`

const RoleBadge = styled.span`
  color: ${COLOR_BRAND_ACCENT};
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const Time = styled.time`
  color: ${COLOR_TEXT_SECONDARY};
  font-size: 11px;
`

const MessageBody = styled.p`
  margin: 3px 0 0;
  color: ${COLOR_TEXT_PRIMARY};
  line-height: 1.45;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`

const Composer = styled.form`
  padding: ${SPACING_MD};
  border-top: 1px solid ${COLOR_UI_BORDER};
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${SPACING_SM};
`

const ComposerInput = styled.textarea`
  min-height: 48px;
  max-height: 150px;
  resize: vertical;
  border: 1px solid ${COLOR_UI_BORDER};
  border-radius: ${RADIUS_MD};
  background: ${COLOR_UI_SURFACE};
  color: ${COLOR_TEXT_STRONG};
  padding: 10px 12px;
  font: inherit;
  &:focus-visible { outline: 2px solid ${COLOR_BRAND_ACCENT}; }
  &:disabled { opacity: 0.5; }
`

const TeamList = styled.ul`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  margin: 0;
  padding: ${SPACING_XS};
  list-style: none;

  @media (max-width: 1024px) {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    align-content: start;
  }
`

const teamMemberLayout = css`
  width: 100%;
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  gap: 9px;
  align-items: center;
  border: 0;
  border-radius: ${RADIUS_MD};
  background: transparent;
  color: ${COLOR_TEXT_PRIMARY};
  padding: 8px;
  text-align: left;
  font: inherit;
`

const TeamListItem = styled.li`
  min-width: 0;
`

const TeamMemberRow = styled.div`
  ${teamMemberLayout}
`

const TeamMemberButton = styled.button`
  ${teamMemberLayout}

  cursor: pointer;
  &:hover:not(:disabled) { background: ${COLOR_UI_SURFACE_HOVER}; }
  &:focus-visible { outline: 2px solid ${COLOR_BRAND_ACCENT}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const ErrorBanner = styled.div`
  grid-column: 1 / -1;
  padding: 8px 12px;
  border: 1px solid ${COLOR_SEMANTIC_ERROR_LIGHT};
  border-radius: ${RADIUS_MD};
  color: ${COLOR_SEMANTIC_ERROR_LIGHT};
  font-size: 12px;
`

function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase()
}

function isMentionBoundary(value: string): boolean {
  return value === '' || !(
    value === '_'
    || /[0-9]/.test(value)
    || value.toLocaleLowerCase() !== value.toLocaleUpperCase()
  )
}

function hasExactMention(
  message: string,
  displayName: string,
  agentDisplayNames: string[],
): boolean {
  const mention = `@${displayName}`
  let offset = message.indexOf(mention)
  while (offset >= 0) {
    const following = message[offset + mention.length] || ''
    const isLongerAgentMention = agentDisplayNames.some((candidate) => {
      if (candidate === displayName || !candidate.startsWith(displayName)) return false
      const longerMention = `@${candidate}`
      return message.startsWith(longerMention, offset)
        && isMentionBoundary(message[offset + longerMention.length] || '')
    })
    if (isMentionBoundary(following) && !isLongerAgentMention) return true
    offset = message.indexOf(mention, offset + mention.length)
  }
  return false
}

function messagingStatus(
  communityStatus: NonNullable<BuzzMessagingState['session']>['community']['status'],
  connectionState: BuzzMessagingState['connectionState'],
): { label: string; tone: MessagingStatusTone } {
  if (communityStatus === 'provisioning') return { label: 'Community provisioning', tone: 'pending' }
  if (communityStatus === 'degraded') return { label: 'Community degraded', tone: 'error' }
  if (communityStatus === 'disabled') return { label: 'Community disabled', tone: 'error' }
  if (connectionState === 'ready') return { label: 'Relay connected', tone: 'ready' }
  if (connectionState === 'error') return { label: 'Relay connection error', tone: 'error' }
  return { label: `Relay ${connectionState}`, tone: 'pending' }
}

export const BuzzMessagingShell: React.FC<BuzzMessagingState> = (props) => {
  const {
    session, selectedChannelId, selectedThreadId, events, publishing, connectionState, error,
    selectChannel, selectThread, createChannel, createThread, publish,
  } = props
  const [channelName, setChannelName] = useState('')
  const [threadTitle, setThreadTitle] = useState('')
  const [message, setMessage] = useState('')
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([])
  const selectedChannel = session?.channels.find((channel) => channel.id === selectedChannelId)
  const selectedThread = session?.threads.find((thread) => thread.id === selectedThreadId)
  const visibleThreads = session?.threads.filter(
    (thread) => thread.channelId === selectedChannelId,
  ) || []
  const sortedMembers = useMemo(() => [...(session?.members || [])].sort((left, right) => (
    left.subjectType.localeCompare(right.subjectType) || left.displayName.localeCompare(right.displayName)
  )), [session?.members])

  if (!session) return null

  const canCreateChannel = session.community.status === 'ready' && connectionState === 'ready'
  const canWrite = canCreateChannel && selectedChannel?.status === 'ready'
  const status = messagingStatus(session.community.status, connectionState)
  const agentDisplayNames = session.members
    .filter((member) => member.subjectType === 'agent')
    .map((member) => member.displayName)

  const submitChannel = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!channelName.trim() || !canCreateChannel) return
    try {
      await createChannel(channelName.trim())
      setChannelName('')
    } catch {
      // The hook exposes the stable error message next to the composer.
    }
  }
  const submitThread = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!threadTitle.trim() || !canWrite) return
    try {
      await createThread(threadTitle.trim())
      setThreadTitle('')
    } catch {
      // The hook exposes the stable error message in the composer banner.
    }
  }
  const submitMessage = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!message.trim() || !selectedThreadId || !canWrite) return
    try {
      await publish(message.trim(), selectedAgentIds)
      setMessage('')
      setSelectedAgentIds([])
    } catch {
      // Keep the draft in place so the user can retry after the hook reports the error.
    }
  }

  return (
    <Shell aria-label="Equa Chat">
      <Rail aria-label="Channels and task threads">
        <RailHeader>
          <CommunityName>{session.community.communityName}</CommunityName>
          <Metadata title={session.community.host}>Buzz community · {session.community.host}</Metadata>
        </RailHeader>
        <NewThreadForm onSubmit={submitChannel}>
          <CompactInput
            aria-label="New channel name"
            placeholder="New channel"
            value={channelName}
            onChange={(event) => setChannelName(event.target.value)}
            disabled={!canCreateChannel}
          />
          <ActionButton type="submit" disabled={!channelName.trim() || !canCreateChannel} aria-label="Create channel">+</ActionButton>
        </NewThreadForm>
        <ChannelList aria-label="Collaboration channels" data-testid="channel-list">
          {session.channels.map((channel) => (
            <CollectionListItem key={channel.id}>
              <ChannelButton
                type="button"
                $active={channel.id === selectedChannelId}
                aria-pressed={channel.id === selectedChannelId}
                aria-label={`${channel.name} channel`}
                onClick={() => selectChannel(channel.id)}
              >
                # {channel.name}
              </ChannelButton>
            </CollectionListItem>
          ))}
        </ChannelList>
        <RailHeader><SectionTitle>Task threads</SectionTitle></RailHeader>
        <NewThreadForm onSubmit={submitThread}>
          <CompactInput
            aria-label="New task thread title"
            placeholder="New task thread"
            value={threadTitle}
            onChange={(event) => setThreadTitle(event.target.value)}
            disabled={!selectedChannel || !canWrite}
          />
          <ActionButton type="submit" disabled={!selectedChannel || !threadTitle.trim() || !canWrite} aria-label="Create task thread">+</ActionButton>
        </NewThreadForm>
        <ThreadList aria-label="Task threads" data-testid="thread-list">
          {visibleThreads.map((thread) => (
            <CollectionListItem key={thread.id}>
              <ThreadButton
                type="button"
                $active={thread.id === selectedThreadId}
                aria-pressed={thread.id === selectedThreadId}
                onClick={() => selectThread(thread.id)}
              >
                {thread.title}
              </ThreadButton>
            </CollectionListItem>
          ))}
        </ThreadList>
      </Rail>

      <Conversation>
        <ConversationHeader>
          <div>
            <SectionTitle>{selectedThread ? 'Task thread' : 'Equa Chat'}</SectionTitle>
            <CommunityName>{selectedThread?.title || 'Create a task thread to begin'}</CommunityName>
            {selectedChannel && <Metadata>#{selectedChannel.name}</Metadata>}
          </div>
          <Status $tone={status.tone} role="status" aria-live="polite">
            {status.label}
          </Status>
        </ConversationHeader>
        <Timeline role="log" aria-live="polite" aria-relevant="additions" aria-atomic="false" aria-label="Task thread timeline">
          {!selectedThread && <EmptyState>{selectedChannel
            ? 'Create a task thread to collaborate with human and AI team members.'
            : 'Create or select a collaboration channel to begin.'}</EmptyState>}
          {selectedThread && events.length === 0 && <EmptyState>No messages yet. Start the task thread below.</EmptyState>}
          {events.map((event) => (
            <Message key={event.id} data-event-id={event.id}>
              <Avatar $agent={event.author.subjectType === 'agent'} aria-hidden="true">
                {initials(event.author.displayName)}
              </Avatar>
              <div>
                <MessageHeader>
                  <Author>{event.author.displayName}</Author>
                  {event.author.subjectType === 'agent' && <RoleBadge>AI team member</RoleBadge>}
                  <Time dateTime={event.createdAt}>{new Date(event.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Time>
                </MessageHeader>
                <MessageBody>{event.content}</MessageBody>
              </div>
            </Message>
          ))}
        </Timeline>
        <Composer onSubmit={submitMessage}>
          {error && <ErrorBanner role="alert">{error}</ErrorBanner>}
          <ComposerInput
            aria-label="Message"
            placeholder={selectedThread ? 'Message your team…' : 'Create a task thread first'}
            value={message}
            onChange={(event) => {
              const nextMessage = event.target.value
              setMessage(nextMessage)
              setSelectedAgentIds((current) => current.filter((subjectId) => {
                const member = session.members.find((candidate) => (
                  candidate.subjectType === 'agent' && candidate.subjectId === subjectId
                ))
                return Boolean(member && hasExactMention(
                  nextMessage, member.displayName, agentDisplayNames,
                ))
              }))
            }}
            disabled={!selectedThread || publishing || !canWrite}
          />
          <ActionButton type="submit" disabled={!selectedThread || !message.trim() || publishing || !canWrite}>
            {publishing ? 'Sending…' : 'Send'}
          </ActionButton>
        </Composer>
      </Conversation>

      <TeamRail aria-label="Community team">
        <RailHeader><SectionTitle>Human + AI team</SectionTitle></RailHeader>
        <TeamList aria-label="Community team members" data-testid="team-list">
          {sortedMembers.map((member) => {
            const content = (
              <>
                <Avatar $agent={member.subjectType === 'agent'} aria-hidden="true">{initials(member.displayName)}</Avatar>
                <span>
                  <Author>{member.displayName}</Author>
                  <Metadata>{member.subjectType === 'agent' ? 'AI team member' : member.role}</Metadata>
                </span>
              </>
            )

            return (
              <TeamListItem key={`${member.subjectType}:${member.subjectId}`}>
                {member.subjectType === 'agent' ? (
                  <TeamMemberButton
                    type="button"
                    disabled={!selectedThread || !canWrite}
                    onClick={() => {
                      setSelectedAgentIds((current) => current.includes(member.subjectId)
                        ? current
                        : [...current, member.subjectId])
                      setMessage((current) => {
                        const mention = `@${member.displayName}`
                        return hasExactMention(current, member.displayName, agentDisplayNames)
                          ? current
                          : `${current}${current ? ' ' : ''}${mention} `
                      })
                    }}
                    title={member.publicKey}
                    aria-pressed={selectedAgentIds.includes(member.subjectId)}
                    aria-label={`${member.displayName}, AI team member; mention in message`}
                    data-subject-id={member.subjectId}
                  >
                    {content}
                  </TeamMemberButton>
                ) : (
                  <TeamMemberRow title={member.publicKey} data-subject-id={member.subjectId}>
                    {content}
                  </TeamMemberRow>
                )}
              </TeamListItem>
            )
          })}
        </TeamList>
      </TeamRail>
    </Shell>
  )
}
